import React, { useEffect, useState } from 'react';
import { Col, Input, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';

import apiCommon from 'api/common';
import apiProjects from 'api/projects';
import downloadFile from 'services/files/downloadFile';
import generateFileHeaders from 'services/files/generateFileHeaders';
import IConfiguration from 'components/interfaces/IConfiguration';
import IRouteParams from 'interfaces/IRouteParams';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { XLXS } from 'services/constants/contentTypes';

import 'style/configurator/src/wallProperties/project/DownloadExcelList.css';
import { useTranslation } from 'react-i18next';
import doorTypes from '../../../configurator/types/doorTypes';
import glazingWallTypes from '../../../configurator/types/glazingWallTypes';
import translations from '../../../../services/translations/translations';
import glassTypes from '../../../configurator/types/glassTypes';

const DownloadExcelList = ({
  configurations,
}: {
  configurations: IConfiguration[];
}): JSX.Element | null => {
  const [excelDownloadType, setExcelDownloadType] = useState('items');
  const { projectId } = useParams<IRouteParams>();
  const [filteredExcelTypes, setFilteredExcelTypes] = useState<string[]>([]);
  const { t } = useTranslation([
    translations.glassWallDoorProperties,
    translations.glassWallProperties,
    translations.woodyWallProperties,
  ]);

  const getExcelListTypes = async () => {
    const result = await apiCommon.excelListTypes.get();
    setFilteredExcelTypes(result);
  };

  useEffect(() => {
    getExcelListTypes();
  }, []);

  const excelTypes = [
    'items',
    'bill-of-quantities',
    'glass-order-all',
    'glass-order-doors',
    'glass-order-walls',
    'picking-order',
    'profile-picking-list',
    'profile-cutting-list',
    'wall-add-ons',
    'painting-order',
  ];

  const excelTypeOptions = (): JSX.Element[] => {
    return excelTypes.map((option) => (
      <option key={option} value={option}>
        {t(`${translations.common}:excelTypes:${option}`)}
      </option>
    ));
  };

  if (configurations.length === 0) {
    return null;
  }

  const downloadGlassesExcel = async () => {
    const selectedExcelLists = configurations
      .filter((config) => {
        return config.isSelected;
      })
      .map((item) => {
        return item.id;
      });

    if (selectedExcelLists.length < 1) {
      return;
    }

    const partTranslations: {
      translations: { [key: string]: string };
      section: string;
    }[] = [
      {
        section: 'doors',
        translations: {},
      },
      {
        section: 'walls',
        translations: {},
      },
    ];
    if (excelDownloadType === 'bill-of-quantities') {
      doorTypes.forEach((type) => {
        partTranslations[0].translations[type] = t(
          `${translations.glassWallDoorProperties}:doorTypes:${type}`
        );
      });
      glazingWallTypes.forEach((type) => {
        partTranslations[1].translations[type] = t(
          `${translations.glassWallProperties}:glazingWallTypes:${type}`
        );
      });
    }
    const glassSeNumbers: string[] = glassTypes.map(
      (type) => window.CommonFunctions.GetGlassItemId(type) ?? ''
    );

    const result = await apiProjects.itemsExcel.get(
      projectId,
      excelDownloadType,
      selectedExcelLists,
      partTranslations,
      glassSeNumbers
    );

    if (excelDownloadType === 'painting-order') {
      downloadFile(
        result.data,
        generateFileHeaders({
          contentType: XLXS,
          fileName: 'items.xlsm',
        })
      );
      return;
    }

    downloadFile(
      result.data,
      generateFileHeaders({
        contentType: XLXS,
        fileName: 'items.xlsx',
      })
    );
  };

  return (
    <Row className="download-excel-list">
      <Col>
        <h2>Download as Excel</h2>
        <Input
          onChange={(e: any) => {
            setExcelDownloadType(e.target.value);
          }}
          type="select"
          value={excelDownloadType}
        >
          {excelTypeOptions().map((excelType) => {
            if (filteredExcelTypes.includes(excelType.props.value)) {
              return excelType;
            }
            return false;
          })}
        </Input>
        <PrimaryButton
          className="download-excel-list-button"
          onClick={downloadGlassesExcel}
          size="xs"
        >
          Download
        </PrimaryButton>
      </Col>
    </Row>
  );
};

export default DownloadExcelList;
